import moment from 'moment';

/**
 * When SAP processes a request in batch (e.g. technical user "BATCHNSCM", or a
 * purchase requisition still being processed), it can return a transient
 * processing error even though a successful acknowledgement may still arrive
 * later. Users should therefore wait up to BATCH_PROCESSING_WAIT_HOURS from the
 * last Sales Order request before raising an incident.
 */
export const BATCH_PROCESSING_KEYWORD = 'User BATCHNSCM';
export const BATCH_PROCESSING_WAIT_HOURS = 48;
export const BATCH_PROCESSING_ASSIGNMENT_GROUP = 'Fact NSCP – L2 Support';

/**
 * Known SAP batch-processing errors. Each entry lists the phrase(s) that must
 * all be present in the rate error message (case-insensitive). Multiple parts
 * are used for messages that embed variable content (e.g. a PR number) between
 * fixed phrases, so exact spacing / values do not need to match.
 */
const BATCH_PROCESSING_PATTERNS: string[][] = [
    ['User BATCHNSCM'],
    ['Purchase requisition', 'is being processed'],
];

/**
 * Returns true when the rate error message is a known SAP batch-processing
 * error. Matching is case-insensitive and tolerant of variable content between
 * fixed phrases, so we are resilient to formatting changes on the SAP side.
 */
export const isBatchProcessingError = (rateErrorMessage?: string | null): boolean => {
    if (!rateErrorMessage) {
        return false;
    }
    const upper = rateErrorMessage.toUpperCase();
    return BATCH_PROCESSING_PATTERNS.some((parts) =>
        parts.every((part) => upper.includes(part.toUpperCase())),
    );
};

export interface IBatchProcessingWaitInfo {
    /** false when lastSORequestSentAt is missing or not a valid date */
    hasTimestamp: boolean;
    /** true once the wait window has fully elapsed (user may raise an incident) */
    canRaiseIncident: boolean;
    /** milliseconds remaining until the wait window elapses (never negative) */
    remainingMs: number;
    /** human readable remaining time, e.g. "12 hrs 34 mins" */
    remainingLabel: string;
    /** formatted timestamp (UTC) when the wait window elapses */
    deadline: string;
    /** formatted timestamp (UTC) of the last Sales Order request */
    lastRequestedAt: string;
}

const formatRemaining = (remainingMs: number): string => {
    // Round up so the countdown reads "1 min" during the final stretch
    // instead of dropping to "0 mins".
    const totalMinutes = Math.ceil(remainingMs / (60 * 1000));
    if (totalMinutes <= 0) {
        return '';
    }
    if (totalMinutes < 60) {
        return `${totalMinutes} min${totalMinutes === 1 ? '' : 's'}`;
    }
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const hoursLabel = `${hours} hr${hours === 1 ? '' : 's'}`;
    return minutes > 0
        ? `${hoursLabel} ${minutes} min${minutes === 1 ? '' : 's'}`
        : hoursLabel;
};

/**
 * Computes how long a user must still wait (relative to "now") before they are
 * allowed to raise an incident for a batch-processing error. The result is
 * recomputed every time this is called, so it always reflects the current time
 * on page refresh or on a live tick.
 */
export const getBatchProcessingWaitInfo = (
    lastSORequestSentAt?: string | null,
    waitHours: number = BATCH_PROCESSING_WAIT_HOURS,
): IBatchProcessingWaitInfo => {
    const sentAt = lastSORequestSentAt ? moment.utc(lastSORequestSentAt) : null;
    if (!sentAt || !sentAt.isValid()) {
        return {
            hasTimestamp: false,
            canRaiseIncident: false,
            remainingMs: 0,
            remainingLabel: '',
            deadline: '',
            lastRequestedAt: '',
        };
    }
    const deadlineMoment = sentAt.clone().add(waitHours, 'hours');
    const remainingMs = Math.max(deadlineMoment.diff(moment.utc()), 0);
    return {
        hasTimestamp: true,
        canRaiseIncident: remainingMs <= 0,
        remainingMs,
        remainingLabel: formatRemaining(remainingMs),
        deadline: deadlineMoment.format('YYYY-MM-DD HH:mm:ss'),
        lastRequestedAt: sentAt.format('YYYY-MM-DD HH:mm:ss'),
    };
};
